import React, { useState, useEffect, useRef } from 'react';
import { Bell, User, ChevronDown, Menu, ArrowLeft, LogOut, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import NotificationDropdown from './NotificationDropdown';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { userProfile, empresa, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Carregar contador de notificações
  useEffect(() => {
    if (userProfile?.empresaCnpj && userProfile?.id) {
      loadNotificationCount();
      
      // Recarregar contador a cada 30 segundos
      const interval = setInterval(loadNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userProfile?.empresaCnpj, userProfile?.id]);

  const loadNotificationCount = async () => {
    if (!userProfile?.empresaCnpj || !userProfile?.id) return;

    try {
      const notificacoes = await firebaseService.getNotificacoesUsuario(
        userProfile.empresaCnpj,
        userProfile.id
      );
      const unreadCount = notificacoes.filter(n => !n.lida).length;
      setNotificationCount(unreadCount);
    } catch (error) {
      console.error('Erro ao carregar contador de notificações:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
      {/* Mobile menu button */}
      <button
        type="button"
        className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden"
        onClick={onMenuClick}
      >
        <span className="sr-only">Abrir sidebar</span>
        <Menu className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Separator */}
      <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 lg:hidden" aria-hidden="true" />

      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
        {/* Logo and title */}
        <div className="flex items-center gap-x-4">
          <div className="flex items-center gap-x-2">
            {empresa?.logoUrl ? (
              <img 
                src={empresa.logoUrl} 
                alt={empresa.nomeFantasia || empresa.razaoSocial || 'Logo'}
                className="h-8 w-8 rounded-lg object-cover"
                onError={(e) => {
                  // Fallback para ícone se a imagem falhar
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    const fallback = parent.querySelector('.logo-fallback') as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div 
              className={`h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center logo-fallback ${empresa?.logoUrl ? 'hidden' : ''}`}
            >
              <span className="text-white font-bold text-sm">
                {empresa?.nomeFantasia?.charAt(0)?.toUpperCase() || empresa?.razaoSocial?.charAt(0)?.toUpperCase() || 'SP'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {empresa?.nomeFantasia || empresa?.razaoSocial || 'SwimmingPool Admin'}
              </span>
              {empresa && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{empresa.cnpj}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-end gap-x-4">
          {/* User info */}
          <div className="flex items-center gap-x-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Bem-vindo,</span>
            <span className="font-medium text-primary-600">{userProfile?.nome || 'Admin'}</span>
          </div>

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              type="button"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative rounded-full bg-white dark:bg-gray-700 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <span className="sr-only">Ver notificações</span>
              <Bell className="h-6 w-6" aria-hidden="true" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-danger-500 text-xs text-white flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <NotificationDropdown
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                notificationCount={notificationCount}
                onCountChange={setNotificationCount}
              />
            )}
          </div>

          {/* Profile dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-x-2 rounded-full bg-white dark:bg-gray-700 p-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <span className="sr-only">Abrir menu do usuário</span>
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                {userProfile?.fotoUrl ? (
                  <img 
                    src={userProfile.fotoUrl} 
                    alt={userProfile.nome}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-primary-600" />
                )}
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </button>

            {/* Dropdown menu */}
            {showUserMenu && (
              <div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userProfile?.nome}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{userProfile?.email}</p>
                  <p className="text-xs text-primary-600 dark:text-primary-400 font-medium">{userProfile?.cargo}</p>
                  {empresa && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">CNPJ: {empresa.cnpj}</p>
                  )}
                </div>
                <Link
                  to="/configuracoes"
                  onClick={() => setShowUserMenu(false)}
                  className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Settings className="h-4 w-4" />
                  Configurações
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <LogOut className="h-4 w-4" />
                  Sair
                </button>
              </div>
            )}
          </div>

          {/* Collapse sidebar button */}
          <button
            type="button"
            className="hidden lg:block rounded-full bg-white dark:bg-gray-700 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <span className="sr-only">Recolher sidebar</span>
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;
